import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertTriggers,
  convertQuestionMappings,
  q,
  v,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = null
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)
  // 防止閃閃
  useEffect(() => {
    const timeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 576
      if (!isMobile) return

      const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const rootEl = document.querySelector('.sd-root-modern')
            if (
              rootEl &&
              !rootEl.classList.contains('sd-root-modern--mobile')
            ) {
              rootEl.classList.add('sd-root-modern--mobile')
            }
          }
        }
      })

      observer.observe(document.getElementById('root'), {
        childList: true,
        subtree: true,
      })

      return () => observer.disconnect()
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 中止條件：
  //  q1 A1 =1(沒有0至未滿3歲子女)
  //  q3 A2 =3(沒有工作)
  //  q4 A3 =1(雇主) 或 =2(自營作業者)
  //  q5 A4 =2(未參加勞保/就保)
  const triggersConditionsInit = convertTriggers([
    {
      type: 'complete',
      expression:
        '({q1} = 1) or ({q3} = 3) or ({q4} = 1) or ({q4} = 2) or ({q5} = 2)',
    },
  ])
  const visibleIfConditionsInit = convertVisibleIf({
    // q3 A2 =1(全時)/2(部份時間) → 顯示 q4 A3、q5 A4
    q4: '({q3} = 1) or ({q3} = 2)',
    q5: '({q3} = 1) or ({q3} = 2)',
    // q8 B3 =2(已婚或同居) 才顯示 q9 B4（配偶工作狀況）；=1/3/4 跳至 C1
    q9: '{q8} = 2',
    // q13 C4 =1(固定班別) 才顯示 q14 C5；=2(輪班)/3(不固定) 跳至 C6
    q14: '{q13} = 1',
    // q16 C7 =1(有申請過) 才顯示 q17 C8（總計幾個月）
    q17: '{q16} = 1',
    // q8 B3 =2(已婚或同居) 才顯示 q18 C9（配偶申請經驗）
    q18: '{q8} = 2',
    // q19 C10（配偶總計幾個月）：需已婚且 q18 C9 =1(有)
    q19: '({q8} = 2) and ({q18} = 1)',
    // q25 E1 =1(知道，有申請過) → 顯示 q26-q33（E2-E9）
    q26: '{q25} = 1',
    q27: '{q25} = 1',
    q28: '{q25} = 1',
    q29: '{q25} = 1',
    q30: '{q25} = 1',
    q31: '{q25} = 1',
    q32: '{q25} = 1',
    q33: '{q25} = 1',
    // q25 E1 =2(知道，沒申請過) → 只顯示 q34 E10（沒申請的原因）；=3(不知道) 直接到 F1
    q34: '{q25} = 2',
    // q35 F1 =1(知道，有申請過) → 顯示 q36-q38（F2-F4）
    q36: '{q35} = 1',
    q37: '{q35} = 1',
    q38: '{q35} = 1',
    // q35 F1 =2(知道，沒申請過) → 只顯示 q39 F5（沒申請的原因）；=3(不知道) 直接到 G1
    q39: '{q35} = 2',
    // q44 G5 =1/2(認同) → 顯示 q45 認同原因；=3(不認同) → 顯示 q46 不認同原因
    q45: '({q44} = 1) or ({q44} = 2)',
    q46: '{q44} = 3',
    // q47 =1(願意填寫並參加抽獎) → 顯示 q48-q50（稱呼/手機/信箱）
    q48: '{q47} = 1',
    q49: '{q47} = 1',
    q50: '{q47} = 1',
  })
  const notRequired = null
  const mappedRateChoice = null
  const pageBreakQuestions = null
  const questionMappings = convertQuestionMappings({})

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  const endMessage = `可惜…目前與您相同特性的樣本額滿，已停止收卷，沒辦法繼續參與本次活動。<br>如果覺得這個主題很有意思，非常歡迎您分享本問卷連結給親友，或是加入我們的<a href='https://lin.ee/HWETcqy' target='_blank'>LINE@</a>，隨時得到最新活動消息。`

  const endSurveyMsg = (survey) => {
    const isTerminated =
      survey.getValue(q(1)) === v(1) ||
      survey.getValue(q(3)) === v(3) ||
      survey.getValue(q(4)) === v(1) ||
      survey.getValue(q(4)) === v(2) ||
      survey.getValue(q(5)) === v(2)

    if (isTerminated) {
      setCompletionMessage(endMessage)
      return
    }

    setCompletionMessage(
      `<div>感謝您的填答～歡迎轉發分享！<br><br>中獎者將於09/11前於<a href="https://www.dsigroup.com.tw/announcement" target="_blank">官網公告</a><br>獎項將以手機簡訊發送，請參加抽獎者留意收件、領取。逾期無法補發。<br><br><br>想參加更多問卷調查活動？手指點點就可以抽獎賺好康！<br>點擊下方按鈕加入 EZChoice APP<br>或複製下載連結 <a href="https://ezcapp.page.link/tAQv" target="_blank">https://ezcapp.page.link/tAQv</a><br>裡面還有更多填問卷拿購物金的活動唷～<br>各種好玩的問卷等你來玩！<br><br>想知道更多好康問卷、活動消息，請追蹤關注<br><a href="https://www.facebook.com/EasyCTRs" target="_blank">【輕鬆點點】粉絲專頁</a><br><br>加入我們的 <a href="https://lin.ee/HWETcqy" target="_blank">LINE@</a>，<br>隨時得到最新填問卷獲好禮的消息。<br><br><h4 style="text-align: center">請按「完成」前往下載 EZChoice 或填寫更多問卷</h4></div>`
    )
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving: requiresLogin.login || webAuthType > 0,
    webAuthType,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
